/**
 * PaneSplitter — the divider between two panes stacked in one dock.
 *
 * Panes could only ever be resized ACROSS the column: the handle on a panel's
 * inner edge changes the dock's width, and every pane in that dock shares it.
 * Stacked panes split the height evenly and there was no way to say "mostly
 * the document, a sliver of the notes".
 *
 * This is the missing axis. It sits BETWEEN two panes, drags vertically, and
 * moves only the pair either side — everything above and below keeps its
 * share, so a three-pane column reads as two independent dividers rather than
 * one elastic stack.
 *
 * TWO THINGS IT DELIBERATELY COPIES FROM `usePanelDrag`:
 *
 *   • THE WINDOW-LEVEL NET. The pointer leaves this 8px strip immediately, so
 *     listening on the element would start a drag that never ends — the exact
 *     freeze that bug fixed for the reorder gesture. Move and release are
 *     bound to the window for the life of the gesture and removed after,
 *     including on pointercancel and Escape.
 *   • FRACTIONS, NOT PIXELS. The delta is divided by the column's measured
 *     height before it reaches the model, so the split means the same thing
 *     on any window (see `PanelSlot.height`).
 *
 * It also stops propagation on pointerdown: the panel header above it is the
 * reorder drag handle, and a splitter press must never be mistaken for one.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

interface Props {
  /** The dock column this divider lives in — measured to convert px → fraction. */
  columnRef: React.RefObject<HTMLElement>;
  /** Fraction of the column, positive = the pane ABOVE grows. */
  onResize: (deltaFraction: number) => void;
  /** Keyboard nudge and accessible bounds come from the pane above. */
  ariaLabel: string;
  disabled?: boolean;
}

/** One arrow press. A twelfth of the column is a visible but unalarming step. */
const KEY_STEP = 1 / 24;

const PaneSplitter: React.FC<Props> = ({ columnRef, onResize, ariaLabel, disabled }) => {
  const [dragging, setDragging] = useState(false);
  const lastY = useRef<number | null>(null);

  const columnHeight = useCallback(
    () => columnRef.current?.getBoundingClientRect().height || 0,
    [columnRef]
  );

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (disabled || (typeof e.button === 'number' && e.button > 0)) return;
    // The header above is the reorder handle; this press is not that.
    e.stopPropagation();
    e.preventDefault();
    lastY.current = e.clientY;
    setDragging(true);
  }, [disabled]);

  useEffect(() => {
    if (!dragging) return;

    const move = (e: PointerEvent) => {
      const height = columnHeight();
      if (!height || lastY.current === null) return;
      const delta = (e.clientY - lastY.current) / height;
      lastY.current = e.clientY;
      if (delta !== 0) onResize(delta);
    };
    const stop = () => { lastY.current = null; setDragging(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') stop(); };

    // Text selection while dragging a divider is never wanted.
    const previousSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    window.addEventListener('keydown', key);
    return () => {
      document.body.style.userSelect = previousSelect;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      window.removeEventListener('keydown', key);
    };
  }, [dragging, columnHeight, onResize]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowUp') { e.preventDefault(); onResize(-KEY_STEP); }
    if (e.key === 'ArrowDown') { e.preventDefault(); onResize(KEY_STEP); }
  }, [disabled, onResize]);

  if (disabled) return null;

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label={ariaLabel}
      tabIndex={0}
      data-testid="pane-splitter"
      data-dragging={dragging}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      className={clsx(
        // Its own row in the column's flex, so it never overlaps a pane's
        // resize edge — the layering mistake that once killed width resize.
        'pointer-events-auto flex-shrink-0 h-2 -my-0.5 flex items-center justify-center',
        'cursor-row-resize group/splitter focus:outline-none'
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'h-0.5 w-10 rounded-full transition-colors duration-200 ease-slide',
          dragging
            ? 'bg-champagne'
            : 'bg-transparent group-hover/splitter:bg-champagne/50 group-focus/splitter:bg-champagne/50'
        )}
      />
    </div>
  );
};

export default PaneSplitter;
